// frontend/src/pages/SelfServicePolicies.js
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, Scale, Megaphone, Users, FileText,
  ChevronRight, CalendarDays, Paperclip, MonitorCog,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { InfoBadge } from '../components/UI/InfoBadge';
import { StatusBadge } from '../components/UI/StatusBadge';
import { usePolicies } from '../hooks/usePoliciesData';

// Each category owns one accent hue so colour becomes a learnable cue.
const CATEGORY = {
  ethics: { label: 'Ethics & Integrity', icon: Scale, dot: 'bg-amber-500', text: 'text-amber-700', soft: 'bg-amber-50', ring: 'ring-amber-100' },
  governance: { label: 'Governance & Speaking Up', icon: Megaphone, dot: 'bg-violet-500', text: 'text-violet-700', soft: 'bg-violet-50', ring: 'ring-violet-100' },
  workplace: { label: 'Workplace & People', icon: Users, dot: 'bg-teal-500', text: 'text-teal-700', soft: 'bg-teal-50', ring: 'ring-teal-100' },
  information_security: { label: 'Information Security & IT', icon: MonitorCog, dot: 'bg-sky-500', text: 'text-sky-700', soft: 'bg-sky-50', ring: 'ring-sky-100' },
  data_protection: { label: 'Data Protection & Privacy', icon: ShieldCheck, dot: 'bg-rose-500', text: 'text-rose-700', soft: 'bg-rose-50', ring: 'ring-rose-100' },
};

const EMPTY_LIST = [];

const PolicyCard = ({ policy }) => {
  const cat = CATEGORY[policy.category] || CATEGORY.ethics;
  const Icon = cat.icon;
  return (
    <Link
      to={`/self-service/policies/${policy.key}`}
      className="group card hover:shadow-lg transition-all duration-200 flex flex-col"
    >
      <div className="card-content flex-1">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full ${cat.soft} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${cat.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-subsection-header leading-snug">{policy.title}</h3>
            <p className="text-small mt-1 line-clamp-2">{policy.summary}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-400 flex-shrink-0 mt-1" />
        </div>
      </div>

      <div className="card-footer gap-3">
        <div className="flex min-w-0 items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <CalendarDays className="w-3.5 h-3.5" /> {policy.effectiveDate || '—'}
          </span>
          {policy.hasPdf && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap" title="Signed copy available">
              <Paperclip className="w-3.5 h-3.5" /> Signed PDF
            </span>
          )}
        </div>
        <div className="ml-auto flex-shrink-0">
          <StatusBadge
            status={policy.acknowledged ? 'acknowledged' : 'pending'}
            label={policy.acknowledged ? 'Acknowledged' : 'To acknowledge'}
            showIcon
          />
        </div>
      </div>
    </Link>
  );
};

const SelfServicePolicies = () => {
  const { data, isLoading } = usePolicies();

  const policies = data?.policies || EMPTY_LIST;
  const categories = data?.categories || EMPTY_LIST;
  const summary = data?.summary || { total: 0, acknowledged: 0, pending: 0 };

  const grouped = useMemo(() => (
    categories
      .map((c) => {
        let items = policies.filter((p) => p.category === c.id);
        // Information Security & IT: always A–Z by title for scannability.
        if (c.id === 'information_security') {
          items = [...items].sort((a, b) =>
            (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }),
          );
        }
        return { ...c, items };
      })
      .filter((c) => c.items.length > 0)
  ), [categories, policies]);

  const pct = summary.total ? Math.round((summary.acknowledged / summary.total) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-12">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Heading row: back anchors title + classification; description stays under the title. */}
            <div className="flex items-start gap-4">
              <Link
                to="/self-service"
                aria-label="Back to Self-Service"
                title="Back to Self-Service"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <h1 className="text-section-header truncate min-w-0">Company Policies</h1>
                  <InfoBadge
                    color="slate"
                    label="Internal"
                    title="Internal — for company use only"
                    className="flex-shrink-0"
                  />
                </div>
                <p className="text-body mt-0.5">
                  HR, workplace, governance and information-security policies. Please read each one and acknowledge it.
                </p>
              </div>
            </div>

            {/* Acknowledgement progress — a standalone block, so it spans the full
                card width rather than inheriting the back-button indent. */}
            {summary.total > 0 && (
              <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">Your acknowledgements</span>
                  <span className="text-gray-500">
                    {summary.acknowledged} of {summary.total} complete
                  </span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full transition-[width] duration-500 ${pct === 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {summary.pending > 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    {summary.pending} {summary.pending === 1 ? 'policy' : 'policies'} still need your acknowledgement.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="card"><div className="card-content py-16"><LoadingSpinner /></div></div>
        ) : grouped.length === 0 ? (
          <div className="card">
            <div className="card-content text-center py-12">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <h3 className="text-subsection-header">No policies published yet</h3>
              <p className="text-body mt-1">Company policies will appear here once HR publishes them.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map((cat) => {
              const c = CATEGORY[cat.id] || CATEGORY.ethics;
              return (
                <section key={cat.id} className="space-y-4">
                  <div className="flex items-center gap-2 px-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                    <h2 className={`text-xs font-semibold uppercase tracking-wider ${c.text}`}>{c.label}</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {cat.items.map((p) => <PolicyCard key={p.key} policy={p} />)}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SelfServicePolicies;
